// ============================================================
// Datei: frontend/src/pages/Benchmark.tsx
// S&P 500 Benchmark — Echte Yahoo Finance Daten since account creation
// ============================================================
import React, { useEffect, useState, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar, Cell, ReferenceLine, Area, AreaChart,
} from 'recharts';
import { sp500Api, accountsApi, Account, BenchmarkComparison, MonthlyComparison, SP500DailyPoint } from '../services/api';
import { useToast } from '../components/Toast';

// ============================================================
// Styles
// ============================================================
const s = {
  page: { padding: 0, maxWidth: '100%' } as React.CSSProperties,
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, flexWrap: 'wrap' as const, gap: 12 } as React.CSSProperties,
  title: { fontSize: 28, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 12 } as React.CSSProperties,
  badge: { background: '#8b5cf6', color: '#fff', fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20 } as React.CSSProperties,
  btnPrimary: { background: '#8b5cf6', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 } as React.CSSProperties,

  priceBar: { display: 'flex', alignItems: 'center', gap: 16, background: '#13172b', border: '1px solid #1e2539', borderRadius: 12, padding: '14px 20px', marginBottom: 24, flexWrap: 'wrap' as const } as React.CSSProperties,
  priceBig: { color: '#f59e0b', fontSize: 28, fontWeight: 800 } as React.CSSProperties,
  liveTag: { background: '#22c55e', color: '#000', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 } as React.CSSProperties,

  heroCard: { background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', border: '2px solid #8b5cf6', borderRadius: 16, padding: 28, marginBottom: 24 } as React.CSSProperties,
  heroTitle: { color: '#a78bfa', fontSize: 18, fontWeight: 700, marginBottom: 20 } as React.CSSProperties,
  heroGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16 } as React.CSSProperties,
  heroItem: { background: '#0d111766', borderRadius: 10, padding: 14 } as React.CSSProperties,
  heroLabel: { color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.5 } as React.CSSProperties,
  heroValue: { color: '#e2e8f0', fontSize: 22, fontWeight: 700, marginTop: 4 } as React.CSSProperties,

  compGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16, marginBottom: 24 } as React.CSSProperties,
  compCard: { borderRadius: 14, padding: 22, textAlign: 'center' as const } as React.CSSProperties,
  compLabel: { fontSize: 12, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 8 } as React.CSSProperties,
  compValue: { fontSize: 28, fontWeight: 800 } as React.CSSProperties,
  compSub: { fontSize: 12, marginTop: 4, opacity: 0.7 } as React.CSSProperties,

  chartBox: { background: '#13172b', border: '1px solid #1e2539', borderRadius: 14, padding: 24, marginBottom: 24 } as React.CSSProperties,
  chartTitle: { color: '#e2e8f0', fontSize: 17, fontWeight: 700, marginBottom: 18 } as React.CSSProperties,
  chartSub: { color: '#64748b', fontSize: 12, marginBottom: 14 } as React.CSSProperties,

  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 16, marginBottom: 24 } as React.CSSProperties,
  statCard: { background: '#13172b', border: '1px solid #1e2539', borderRadius: 14, padding: 20 } as React.CSSProperties,
  statTitle: { color: '#9ca3af', fontSize: 13, fontWeight: 600, marginBottom: 14 } as React.CSSProperties,
  statRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #1e253944' } as React.CSSProperties,
  statLabel: { color: '#94a3b8', fontSize: 13 } as React.CSSProperties,
  statValue: { fontSize: 14, fontWeight: 700 } as React.CSSProperties,

  table: { width: '100%', borderCollapse: 'collapse' as const } as React.CSSProperties,
  th: { color: '#64748b', fontSize: 11, textTransform: 'uppercase' as const, padding: '10px 12px', borderBottom: '1px solid #1e2539', textAlign: 'left' as const, fontWeight: 600 } as React.CSSProperties,
  td: { color: '#d1d5db', padding: '10px 12px', borderBottom: '1px solid #1e253933', fontSize: 13 } as React.CSSProperties,

  loading: { textAlign: 'center' as const, color: '#6b7280', padding: 80, fontSize: 18 } as React.CSSProperties,
  info: { background: '#1e293b44', border: '1px solid #334155', borderRadius: 12, padding: 16, marginTop: 20, fontSize: 13, color: '#64748b', lineHeight: 1.6 } as React.CSSProperties,
};

// ============================================================
// Component
// ============================================================
const Benchmark: React.FC = () => {
  const toast = useToast();
  const [account, setAccount] = useState<Account | null>(null);
  const [benchmark, setBenchmark] = useState<BenchmarkComparison | null>(null);
  const [spPrice, setSpPrice] = useState<{ price: number; change: number; change_percent: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [accRes, priceRes] = await Promise.allSettled([
        accountsApi.list(),
        sp500Api.getCurrentPrice(),
      ]);

      if (priceRes.status === 'fulfilled') setSpPrice(priceRes.value.data);

      if (accRes.status === 'fulfilled') {
        const active = accRes.value.data.find((a: Account) => a.is_active) || accRes.value.data[0];
        if (!active) { setError('No account found'); setLoading(false); return; }
        setAccount(active);

        const benchRes = await sp500Api.getBenchmark(active.id);
        setBenchmark(benchRes.data);
      }
    } catch (e: any) {
      const msg = e?.response?.data || e?.message || 'Failed to load';
      setError(msg);
      toast.error('Benchmark loading failed', 'Yahoo Finance data could not be retrieved.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div style={s.loading}>⏳ Loading S&P 500 data from Yahoo Finance...</div>;
  if (error) return <div style={s.loading}>❌ {error}</div>;
  if (!benchmark) return <div style={s.loading}>No data available</div>;

  const b = benchmark;
  const hasTradeData = b.chart_data.length > 0;
  const hasSPChart = b.sp500_daily_chart.length > 0;

  // SP500 täglicher Chart
  const spChartData = b.sp500_daily_chart.map((d: SP500DailyPoint) => ({
    date: d.date.substring(5),
    fullDate: d.date,
    close: d.close,
    equity: d.sp500_equity,
    returnPct: d.return_pct,
  }));

  const spMin = spChartData.length > 0 ? Math.floor(Math.min(...spChartData.map((d: { close: number }) => d.close)) / 50) * 50 : 0;
  const spMax = spChartData.length > 0 ? Math.ceil(Math.max(...spChartData.map((d: { close: number }) => d.close)) / 50) * 50 : 10000;

  const monthlyChartData = b.monthly_comparison.map((m: MonthlyComparison) => ({
    month: m.month.substring(5),
    user: m.user_return_pct,
    sp500: m.sp500_return_pct,
    alpha: m.alpha,
  }));

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <h1 style={s.title}>
          📈 S&P 500 Benchmark
          {account && <span style={s.badge}>{account.name}</span>}
        </h1>
        <button style={s.btnPrimary} onClick={loadData}>🔄 Refresh</button>
      </div>

      {/* Live Price Bar */}
      {spPrice && (
        <div style={s.priceBar}>
          <span style={s.liveTag}>LIVE</span>
          <span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>S&P 500</span>
          <span style={s.priceBig}>${spPrice.price.toLocaleString()}</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: spPrice.change >= 0 ? '#22c55e' : '#ef4444' }}>
            {spPrice.change >= 0 ? '+' : ''}{spPrice.change.toFixed(2)} ({spPrice.change_percent >= 0 ? '+' : ''}{spPrice.change_percent.toFixed(2)}%)
          </span>
          <span style={{ color: '#475569', fontSize: 11, marginLeft: 'auto' }}>
            Start Price ({b.account_created}): <strong style={{ color: '#f59e0b' }}>${b.sp500_start_price.toLocaleString()}</strong>
            {' '}• Yahoo Finance
          </span>
        </div>
      )}

      {/* S&P 500 Chart since account creation */}
      {hasSPChart && (
        <div style={s.chartBox}>
          <div style={s.chartTitle}>📊 S&P 500 Index since account creation ({b.account_created})</div>
          <div style={s.chartSub}>
            {b.sp500_daily_chart.length} Trading days •
            Start: ${b.sp500_start_price.toLocaleString()} •
            Current: ${b.sp500_current_price.toLocaleString()} •
            <span style={{ color: b.sp500_total_return_pct >= 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
              {' '}{b.sp500_total_return_pct >= 0 ? '+' : ''}{b.sp500_total_return_pct.toFixed(2)}%
            </span>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={spChartData}>
              <defs>
                <linearGradient id="spGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} interval={Math.max(1, Math.floor(spChartData.length / 10))} />
              <YAxis domain={[spMin, spMax]} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v: number) => v.toLocaleString()} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #f59e0b44', borderRadius: 10, fontSize: 13 }}
                labelFormatter={(_: string, payload: any[]) => payload?.[0]?.payload?.fullDate || ''}
                formatter={(value: number) => [`$${value.toLocaleString()}`, 'S&P 500']}
              />
              <Area type="monotone" dataKey="close" stroke="#f59e0b" strokeWidth={2} fill="url(#spGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Benchmark Vergleich — nur mit Trades */}
      {hasTradeData ? (
        <>
          {/* Hero Card */}
          <div style={s.heroCard}>
            <div style={s.heroTitle}>
              {b.outperforming ? '🏆 You are beating the market!' : '📉 The market is ahead'}
            </div>
            <div style={s.heroGrid}>
              <div style={s.heroItem}>
                <div style={s.heroLabel}>YOUR RETURN</div>
                <div style={{ ...s.heroValue, color: b.user_total_return_pct >= 0 ? '#22c55e' : '#ef4444' }}>
                  {b.user_total_return_pct >= 0 ? '+' : ''}{b.user_total_return_pct.toFixed(2)}%
                </div>
              </div>
              <div style={s.heroItem}>
                <div style={s.heroLabel}>S&P 500 RETURN</div>
                <div style={{ ...s.heroValue, color: b.sp500_total_return_pct >= 0 ? '#22c55e' : '#ef4444' }}>
                  {b.sp500_total_return_pct >= 0 ? '+' : ''}{b.sp500_total_return_pct.toFixed(2)}%
                </div>
              </div>
              <div style={s.heroItem}>
                <div style={s.heroLabel}>ALPHA</div>
                <div style={{ ...s.heroValue, color: b.alpha >= 0 ? '#22c55e' : '#ef4444' }}>
                  {b.alpha >= 0 ? '+' : ''}{b.alpha.toFixed(2)}%
                </div>
              </div>
              <div style={s.heroItem}>
                <div style={s.heroLabel}>ACCOUNT SINCE</div>
                <div style={s.heroValue}>{b.account_created}</div>
              </div>
              <div style={s.heroItem}>
                <div style={s.heroLabel}>PERIOD</div>
                <div style={s.heroValue}>{b.period_days} Days</div>
              </div>
              <div style={s.heroItem}>
                <div style={s.heroLabel}>TRADES</div>
                <div style={s.heroValue}>{b.user_total_trades}</div>
              </div>
            </div>
          </div>

          {/* Comparison Cards */}
          <div style={s.compGrid}>
            <div style={{ ...s.compCard, background: '#13172b', border: '1px solid #8b5cf6' }}>
              <div style={{ ...s.compLabel, color: '#a78bfa' }}>Your Portfolio</div>
              <div style={{ ...s.compValue, color: b.user_total_pnl >= 0 ? '#22c55e' : '#ef4444' }}>
                ${b.user_final_equity.toLocaleString()}
              </div>
              <div style={{ ...s.compSub, color: b.user_total_pnl >= 0 ? '#22c55e' : '#ef4444' }}>
                {b.user_total_pnl >= 0 ? '+' : ''}${b.user_total_pnl.toFixed(2)}
              </div>
            </div>
            <div style={{ ...s.compCard, background: '#13172b', border: '1px solid #f59e0b' }}>
              <div style={{ ...s.compLabel, color: '#f59e0b' }}>S&P 500 (Buy & Hold)</div>
              <div style={{ ...s.compValue, color: b.sp500_total_pnl >= 0 ? '#22c55e' : '#ef4444' }}>
                ${b.sp500_final_value.toLocaleString()}
              </div>
              <div style={{ ...s.compSub, color: b.sp500_total_pnl >= 0 ? '#22c55e' : '#ef4444' }}>
                {b.sp500_total_pnl >= 0 ? '+' : ''}${b.sp500_total_pnl.toFixed(2)}
              </div>
            </div>
            <div style={{ ...s.compCard, background: b.outperforming ? '#22c55e11' : '#ef444411', border: `1px solid ${b.outperforming ? '#22c55e' : '#ef4444'}` }}>
              <div style={{ ...s.compLabel, color: b.outperforming ? '#22c55e' : '#ef4444' }}>
                {b.outperforming ? '🏆 Outperformance' : '📉 Underperformance'}
              </div>
              <div style={{ ...s.compValue, color: b.alpha >= 0 ? '#22c55e' : '#ef4444' }}>
                {b.alpha >= 0 ? '+' : ''}{b.alpha.toFixed(2)}%
              </div>
              <div style={{ ...s.compSub, color: '#94a3b8' }}>Alpha vs S&P 500</div>
            </div>
          </div>

          {/* Equity Comparison Chart */}
          <div style={s.chartBox}>
            <div style={s.chartTitle}>📊 Equity Comparison — Portfolio vs S&P 500 Buy & Hold</div>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={b.chart_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="trade_num" tick={{ fontSize: 11 }} tickFormatter={(v: number) => v === 0 ? 'Start' : `#${v}`} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(1)}k`} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, fontSize: 13 }}
                  labelFormatter={(v: number) => v === 0 ? 'Start' : `Trade #${v}`}
                  formatter={(value: number, name: string) => [
                    `$${value.toFixed(2)}`,
                    name === 'user_equity' ? '📈 Portfolio' : '📊 S&P 500'
                  ]}
                />
                <Legend formatter={(value: string) => value === 'user_equity' ? '📈 Portfolio' : '📊 S&P 500 (Buy & Hold)'} />
                <Line type="monotone" dataKey="user_equity" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="sp500_equity" stroke="#f59e0b" strokeWidth={2} strokeDasharray="8 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Return % */}
          <div style={s.chartBox}>
            <div style={s.chartTitle}>📈 Cumulative Return (%)</div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={b.chart_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="trade_num" tick={{ fontSize: 11 }} tickFormatter={(v: number) => v === 0 ? '' : `#${v}`} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, fontSize: 13 }}
                  formatter={(value: number, name: string) => [
                    `${value.toFixed(2)}%`,
                    name === 'user_return_pct' ? '📈 Portfolio' : '📊 S&P 500'
                  ]}
                />
                <ReferenceLine y={0} stroke="#475569" strokeDasharray="3 3" />
                <Legend formatter={(value: string) => value === 'user_return_pct' ? '📈 Portfolio' : '📊 S&P 500'} />
                <Line type="monotone" dataKey="user_return_pct" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="sp500_return_pct" stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 3" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly Alpha */}
          {monthlyChartData.length > 0 && (
            <div style={s.chartBox}>
              <div style={s.chartTitle}>📅 Monthly Alpha</div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, fontSize: 13 }} formatter={(value: number) => [`${value.toFixed(2)}%`]} />
                  <ReferenceLine y={0} stroke="#475569" />
                  <Bar dataKey="alpha" name="Alpha">
                    {monthlyChartData.map((entry: { alpha: number }, index: number) => (
                      <Cell key={index} fill={entry.alpha >= 0 ? '#22c55e' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Stats */}
          <div style={s.statsGrid}>
            <div style={s.statCard}>
              <div style={s.statTitle}>📈 Your Performance</div>
              {([
                ['Total Return', `${b.user_total_return_pct >= 0 ? '+' : ''}${b.user_total_return_pct.toFixed(2)}%`, b.user_total_return_pct >= 0],
                ['Final Equity', `$${b.user_final_equity.toLocaleString()}`, true],
                ['Total PnL', `${b.user_total_pnl >= 0 ? '+' : ''}$${b.user_total_pnl.toFixed(2)}`, b.user_total_pnl >= 0],
                ['Sharpe Ratio', b.user_sharpe.toFixed(2), b.user_sharpe > 1],
                ['Max Drawdown', `${b.user_max_drawdown.toFixed(2)}%`, false],
                ['Win Rate', `${b.user_win_rate.toFixed(1)}%`, b.user_win_rate >= 50],
                ['Trades', String(b.user_total_trades), true],
              ] as [string, string, boolean][]).map(([label, value, pos]) => (
                <div key={label} style={s.statRow}>
                  <span style={s.statLabel}>{label}</span>
                  <span style={{ ...s.statValue, color: pos ? '#22c55e' : '#ef4444' }}>{value}</span>
                </div>
              ))}
            </div>
            <div style={s.statCard}>
              <div style={s.statTitle}>📊 S&P 500 (Buy & Hold ab {b.account_created})</div>
              {([
                ['Total Return', `${b.sp500_total_return_pct >= 0 ? '+' : ''}${b.sp500_total_return_pct.toFixed(2)}%`, b.sp500_total_return_pct >= 0],
                ['Final Value', `$${b.sp500_final_value.toLocaleString()}`, true],
                ['Total PnL', `${b.sp500_total_pnl >= 0 ? '+' : ''}$${b.sp500_total_pnl.toFixed(2)}`, b.sp500_total_pnl >= 0],
                ['Ann. Return', `${b.sp500_annualized_return.toFixed(2)}%`, true],
                ['Start Price', `$${b.sp500_start_price.toLocaleString()}`, true],
                ['Current', `$${b.sp500_current_price.toLocaleString()}`, true],
                ['Data Source', 'Yahoo Finance', true],
              ] as [string, string, boolean][]).map(([label, value, pos]) => (
                <div key={label} style={s.statRow}>
                  <span style={s.statLabel}>{label}</span>
                  <span style={{ ...s.statValue, color: pos ? '#22c55e' : '#94a3b8' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Table */}
          {b.monthly_comparison.length > 0 && (
            <div style={s.statCard}>
              <div style={s.statTitle}>📅 Monthly Comparison</div>
              <div className="table-scroll">
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>MONTH</th>
                      <th style={s.th}>PORTFOLIO</th>
                      <th style={s.th}>S&P 500</th>
                      <th style={s.th}>ALPHA</th>
                      <th style={s.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.monthly_comparison.map((m: MonthlyComparison) => (
                      <tr key={m.month}>
                        <td style={{ ...s.td, fontWeight: 600 }}>{m.month}</td>
                        <td style={{ ...s.td, color: m.user_return_pct >= 0 ? '#22c55e' : '#ef4444' }}>
                          {m.user_return_pct >= 0 ? '+' : ''}{m.user_return_pct.toFixed(2)}%
                        </td>
                        <td style={{ ...s.td, color: m.sp500_return_pct >= 0 ? '#22c55e' : '#ef4444' }}>
                          {m.sp500_return_pct >= 0 ? '+' : ''}{m.sp500_return_pct.toFixed(2)}%
                        </td>
                        <td style={{ ...s.td, color: m.alpha >= 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
                          {m.alpha >= 0 ? '+' : ''}{m.alpha.toFixed(2)}%
                        </td>
                        <td style={s.td}>{m.alpha >= 0 ? '🏆' : '📉'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ ...s.chartBox, textAlign: 'center' as const, padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
          <h3 style={{ color: '#94a3b8', marginBottom: 8 }}>Benchmark ready — add trades!</h3>
          <p style={{ color: '#64748b', fontSize: 14 }}>
            The S&P 500 chart above shows the market since your account creation ({b.account_created}).
            Once you add trades, your performance will be automatically compared.
          </p>
        </div>
      )}

      {/* Info */}
      <div style={s.info}>
        <strong style={{ color: '#94a3b8' }}>ℹ️ Data Source:</strong> S&P 500 price data live from
        <strong style={{ color: '#f59e0b' }}> Yahoo Finance</strong> (^GSPC) since account creation ({b.account_created}).
        Start Price: ${b.sp500_start_price.toLocaleString()} • Cache: 1h •
        The benchmark compares your portfolio with buy-and-hold in the S&P 500 using your start capital (${b.start_capital.toLocaleString()}).
      </div>
    </div>
  );
};

export default Benchmark;